import React from 'react';
import { GitMerge, AlertCircle, FileEdit } from 'lucide-react';
import TensionEditor from './TensionEditor';
import { StatusChip, findCircle, formatDate, parseOperations, sameId, tensionStatus } from './governance';

// Regroupement de « Mes tensions » : à rédiger, en cours de décision, historique.
const GROUPS = [
  { key: 'draft', title: 'À rédiger', statuses: ['draft', 'reworked', 'failed'] },
  { key: 'pending', title: 'En cours de décision', statuses: ['proposal', 'meeting'] },
  { key: 'history', title: 'Historique', statuses: ['applied', 'rejected'] },
];

const TensionsPanel = ({
  selectedCircle,
  tensions,
  tensionsLoading,
  tensionError,
  selectedTensionId,
  circles,
  tensionDraft,
  tensionVotes,
  tensionVotesLoading,
  onStartTension,
  onSelectTension,
  isFieldChanged,
  onUpdateField,
  onUpdateProposedField,
  onUpdateList,
  onChangeAction,
  onDelete,
  onPropose,
  onWithdraw,
  onCancel,
  onSave,
  user,
}) => {
  const canStartTension = Boolean(selectedCircle?.id);
  const grouped = GROUPS.map((group) => ({
    ...group,
    items: (tensions || []).filter((t) => group.statuses.includes(tensionStatus(t).key)),
  }));

  const renderItem = (tension) => {
    const updatedAt = tension.updated_at || tension.created_at;
    const ops = parseOperations(tension.operations);
    const circle = findCircle(circles, tension.circle_id);
    const isMeeting = tensionStatus(tension).key === 'meeting';
    const selected = sameId(selectedTensionId, tension.id);
    const content = (
      <>
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold text-slate-900 text-sm min-w-0 truncate">
            {tension.title || 'Tension sans titre'}
          </div>
          <StatusChip tension={tension} className="shrink-0" />
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Cercle concerné : {circle?.name || tension.original_data?.name || '—'}
          {ops.length > 1 ? ` · ${ops.length} changements` : ''}
        </p>
        {isMeeting && (
          <p className="text-[11px] text-indigo-600 mt-1">Traitée en réunion de gouvernance.</p>
        )}
        {updatedAt && (
          <p className="text-[10px] text-slate-400 mt-1">Mise à jour le {formatDate(updatedAt)}</p>
        )}
      </>
    );
    if (isMeeting) {
      // Le secrétaire de réunion la tient : pas d'édition depuis cet onglet.
      return (
        <div key={tension.id} className="w-full text-left p-3 rounded-xl border border-slate-200 bg-white">
          {content}
        </div>
      );
    }
    return (
      <button
        key={tension.id}
        type="button"
        onClick={() => onSelectTension(tension)}
        className={`w-full text-left p-3 rounded-xl border transition hover:border-purple-200 hover:bg-white ${
          selected ? 'border-purple-400 bg-white shadow-sm' : 'border-slate-200 bg-slate-100'
        }`}
      >
        {content}
      </button>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-hidden flex flex-col">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-purple-700 font-semibold">
            <GitMerge size={16} />
            Tensions de gouvernance
          </div>
          <h2 className="text-xl font-bold text-slate-900">Mes tensions</h2>
          <p className="text-slate-500 text-sm">
            1. Rédige ce qui coince et ta proposition · 2. Propose-la au vote ou porte-la en réunion de gouvernance ·
            3. Acceptée, la structure est mise à jour. Le premier lien et la stratégie ne se changent pas ici.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => onStartTension(selectedCircle)}
            className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center disabled:opacity-60"
            disabled={!canStartTension}
            title={
              canStartTension
                ? `Nouveau brouillon sur « ${selectedCircle.name} »`
                : 'Sélectionne un cercle ou un rôle dans la structure pour lancer une tension'
            }
          >
            <FileEdit size={14} className="mr-1" />
            Nouvelle tension
          </button>
        </div>
      </div>

      {tensionError && (
        <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-xl">
          <AlertCircle size={16} />
          {tensionError}
        </div>
      )}

      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        <div className="col-span-12 md:col-span-5 bg-slate-50 border border-slate-100 rounded-xl p-3 overflow-y-auto">
          {tensionsLoading && (tensions || []).length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">Chargement…</div>
          ) : (tensions || []).length === 0 ? (
            <div className="text-center text-slate-400 text-sm space-y-2 py-6">
              <p>Aucune tension enregistrée.</p>
              <p className="text-xs">Sélectionne un cercle ou un rôle, puis clique sur « Nouvelle tension ».</p>
            </div>
          ) : (
            <div className="space-y-4">
              {grouped.filter((group) => group.items.length > 0).map((group) => (
                <div key={group.key} className="space-y-2">
                  <p className="text-xs uppercase font-semibold text-slate-500">
                    {group.title} ({group.items.length})
                  </p>
                  {group.items.map(renderItem)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-7 bg-white border border-slate-100 rounded-xl p-4 shadow-inner overflow-y-auto">
          <TensionEditor
            tensionDraft={tensionDraft}
            circles={circles}
            user={user}
            tensionVotes={tensionVotes}
            tensionVotesLoading={tensionVotesLoading}
            tensionsLoading={tensionsLoading}
            isFieldChanged={isFieldChanged}
            onUpdateField={onUpdateField}
            onUpdateProposedField={onUpdateProposedField}
            onUpdateList={onUpdateList}
            onChangeAction={onChangeAction}
            onDelete={onDelete}
            onPropose={onPropose}
            onWithdraw={onWithdraw}
            onCancel={onCancel}
            onSave={onSave}
            defaultCircleId={selectedCircle?.id}
          />
        </div>
      </div>
    </div>
  );
};

export default TensionsPanel;
